import { Link } from "@inertiajs/react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock,
  Loader2,
  type LucideIcon,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import {
  AnalysisProgressBar,
  type AnalysisStatus,
} from "@/components/analysis/AnalysisProgressBar";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useInsightRequestChannel } from "@/hooks/useInsightRequestChannel";
import { bizminer_app_pillar_insight_path } from "@/routes";
import type {
  BizminerAnalysisWebhookEvent,
  BizminerInsightRequest,
  BizminerPillarInsight,
} from "@/types/serializers";

interface Props {
  pillar_insight: BizminerPillarInsight;
  insight_request: BizminerInsightRequest;
  webhook_events: Array<BizminerAnalysisWebhookEvent>;
}

const statusConfig: Record<string, { label: string; icon: LucideIcon }> = {
  collecting_data: { label: "Collecting Data", icon: Circle },
  validating: { label: "Validating", icon: Circle },
  analyzing: { label: "Analyzing", icon: Circle },
  analyzed: { label: "Analyzed", icon: CheckCircle2 },
  completed: { label: "Completed", icon: CheckCircle2 },
  failed: { label: "Failed", icon: XCircle },
};

// Status card with icon and action button
interface StatusCardProps {
  status: string;
  engineStatus: string | null;
  failedAtEngineStatus: string | null;
  isInProgress: boolean;
  pillarInsightId: number;
  errorMessage?: string | null;
}

const StatusCard = ({
  status,
  engineStatus,
  failedAtEngineStatus,
  isInProgress,
  pillarInsightId,
  errorMessage,
}: StatusCardProps) => {
  const showProgressBar =
    isInProgress || status === "failed" || status === "analyzed";
  const progressStatus: AnalysisStatus =
    status === "failed"
      ? "failed"
      : status === "analyzed"
        ? "analyzed"
        : status === "completed"
          ? "completed"
          : (engineStatus as AnalysisStatus) || "collecting_data";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isInProgress && <Clock className="h-5 w-5" />}

          {status === "completed" && (
            <CheckCircle2 className="h-5 w-5 text-chart-6" />
          )}
          {status === "failed" && (
            <XCircle className="h-5 w-5 text-destructive" />
          )}
          <span className="capitalize">{status}</span>
        </CardTitle>
        <CardDescription>
          {isInProgress && "Your analysis is being processed. "}
          {status === "analyzed" &&
            "Ingesting data and preparing your insights..."}
          {status === "completed" && "Your analysis is complete!"}
          {status === "failed" && "Analysis failed. Please contact support."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showProgressBar && (
          <AnalysisProgressBar
            status={progressStatus}
            failedAtStage={failedAtEngineStatus}
            showLabels={true}
          />
        )}

        {status === "analyzed" && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Processing data...</span>
          </div>
        )}
        {status === "failed" && errorMessage && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive font-medium">
              Error Details:
            </p>
            <p className="text-sm text-muted-foreground mt-1">{errorMessage}</p>
          </div>
        )}
        {status === "completed" && (
          <Link
            href={bizminer_app_pillar_insight_path(pillarInsightId)}
            className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            View Pillar Insight
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

// Progress timeline showing webhook events
interface ProgressTimelineProps {
  events: BizminerAnalysisWebhookEvent[];
}

const ProgressTimeline = ({ events }: ProgressTimelineProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (events.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Progress Timeline</CardTitle>
            <CardDescription>
              Webhook events received from the analysis engine
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Hide
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-2" />
                Show Details
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <div className="space-y-4">
            {events.map((event, index) => {
              const config =
                statusConfig[event.status] || statusConfig.collecting_data;
              const IconComponent = config.icon;
              const isLatest = index === 0;

              return (
                <div key={event.id} className="flex items-start gap-3">
                  <div
                    className={`mt-1 ${isLatest ? "text-primary" : "text-muted-foreground"}`}
                  >
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-medium ${isLatest ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {config.label}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(event.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Request details card
interface RequestDetailsProps {
  insightRequest: BizminerInsightRequest;
}

const RequestDetails = ({ insightRequest }: RequestDetailsProps) => {
  const competitors = insightRequest.request_data.competitors as
    | Array<{ name: string; url: string }>
    | undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Request Details</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-muted-foreground">Company Name</dt>
            <dd className="font-medium">
              {insightRequest.request_data.company_name}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Website</dt>
            <dd className="font-medium">
              {insightRequest.request_data.website}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Business Line</dt>
            <dd className="font-medium">
              {insightRequest.request_data.business_line}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Country</dt>
            <dd className="font-medium">
              {insightRequest.request_data.country}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Submitted</dt>
            <dd className="font-medium">
              {new Date(insightRequest.submitted_at!).toLocaleString()}
            </dd>
          </div>
          {insightRequest.completed_at && (
            <div>
              <dt className="text-muted-foreground">Completed</dt>
              <dd className="font-medium">
                {new Date(insightRequest.completed_at).toLocaleString()}
              </dd>
            </div>
          )}
        </dl>

        {competitors && competitors.length > 0 && (
          <div className="mt-4">
            <dt className="text-muted-foreground mb-2">Competitors</dt>
            <dd className="space-y-2">
              {competitors.map((competitor, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="font-medium">{competitor.name}</span>
                  <span className="text-muted-foreground">-</span>
                  <a
                    href={competitor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {competitor.url}
                  </a>
                </div>
              ))}
            </dd>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Show({
  pillar_insight,
  insight_request: initialInsightRequest,
  webhook_events: initialWebhookEvents,
}: Props) {
  const { insightRequest, newEvents, isConnected } = useInsightRequestChannel(
    initialInsightRequest,
  );

  if (!insightRequest) {
    return;
  }

  // Merge initial webhook events with new events from Action Cable
  const existingIds = new Set(initialWebhookEvents.map((e) => e.id));
  const uniqueNewEvents = newEvents.filter((e) => !existingIds.has(e.id));
  const allWebhookEvents = [...uniqueNewEvents, ...initialWebhookEvents];

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <ConnectionStatus
            status={isConnected ? "connected" : "disconnected"}
          />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Analysis Status
          </h1>
          <p className="text-muted-foreground mt-2">{pillar_insight.title}</p>
        </div>

        <StatusCard
          status={insightRequest.status}
          engineStatus={insightRequest.engine_status}
          failedAtEngineStatus={insightRequest.failed_at_engine_status}
          isInProgress={insightRequest.in_progress}
          pillarInsightId={pillar_insight.id}
          errorMessage={insightRequest.error_message}
        />

        <ProgressTimeline events={allWebhookEvents} />

        <RequestDetails insightRequest={insightRequest} />
      </div>
    </BizminerLayout>
  );
}
